// src/contexts/OfflineContext.tsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { syncManagerService } from '../services/offline/syncManager.service';
import { syncQueueService } from '../services/offline/syncQueue.service';
import { conflictResolverService, ConflictInfo } from '../services/offline/conflictResolver.service';
import { indexedDBService } from '../services/offline/indexedDB.service';
import { SyncQueueItem } from '../types/offline.types';

interface OfflineContextValue {
  // Online status
  isOnline: boolean;

  // Sync status
  isSyncing: boolean;
  pendingCount: number;
  lastSync: Date | null;

  // Conflicts
  conflicts: SyncQueueItem[];
  conflictCount: number;

  // Actions
  sync: () => Promise<void>;
  downloadForOffline: (projectId: string) => Promise<void>;
  resolveConflict: (
    conflictId: string,
    resolution: 'local' | 'server' | 'merge',
    mergedData?: any
  ) => Promise<void>;
  clearOfflineData: () => Promise<void>;

  // Stats
  offlineStats: {
    workloads: number;
    addresses: number;
    surveys: number;
    responses: number;
  } | null;
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

export const OfflineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [conflicts, setConflicts] = useState<SyncQueueItem[]>([]);
  const [offlineStats, setOfflineStats] = useState<{
    workloads: number;
    addresses: number;
    surveys: number;
    responses: number;
  } | null>(null);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger sync when coming back online
      syncManagerService.sync();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Subscribe to sync events
  useEffect(() => {
    const unsubscribe = syncManagerService.subscribe((event) => {
      switch (event.type) {
        case 'sync-start':
          setIsSyncing(true);
          break;
        case 'sync-complete':
          setIsSyncing(false);
          setLastSync(new Date());
          updatePendingCount();
          break;
        case 'sync-error':
          setIsSyncing(false);
          break;
        case 'conflict':
          updateConflicts();
          break;
      }
    });

    // Start auto-sync
    syncManagerService.startAutoSync();

    return () => {
      unsubscribe();
      syncManagerService.stopAutoSync();
    };
  }, []);

  // Initial load of pending count and conflicts
  useEffect(() => {
    updatePendingCount();
    updateConflicts();
    updateStats();
  }, []);

  const updatePendingCount = useCallback(async () => {
    const pending = await syncQueueService.getPending();
    setPendingCount(pending.length);
  }, []);

  const updateConflicts = useCallback(async () => {
    const conflictItems = await conflictResolverService.getConflicts();
    setConflicts(conflictItems);
  }, []);

  const updateStats = useCallback(async () => {
    try {
      const stats = await indexedDBService.getStats();
      setOfflineStats({
        workloads: stats.workloads,
        addresses: stats.addresses,
        surveys: stats.surveys,
        responses: stats.responses,
      });
    } catch (error) {
      console.error('Error getting offline stats:', error);
    }
  }, []);

  const sync = useCallback(async () => {
    await syncManagerService.forceSync();
    await updatePendingCount();
    await updateConflicts();
    await updateStats();
  }, [updatePendingCount, updateConflicts, updateStats]);

  const downloadForOffline = useCallback(async (projectId: string) => {
    await syncManagerService.downloadForOffline(projectId);
    await updateStats();
  }, [updateStats]);

  const resolveConflict = useCallback(
    async (
      conflictId: string,
      resolution: 'local' | 'server' | 'merge',
      mergedData?: any
    ) => {
      await conflictResolverService.resolve(conflictId, resolution, mergedData);
      await updateConflicts();
      await updatePendingCount();
    },
    [updateConflicts, updatePendingCount]
  );

  const clearOfflineData = useCallback(async () => {
    await syncManagerService.clearOfflineData();
    setPendingCount(0);
    setConflicts([]);
    setOfflineStats(null);
  }, []);

  const value: OfflineContextValue = {
    isOnline,
    isSyncing,
    pendingCount,
    lastSync,
    conflicts,
    conflictCount: conflicts.length,
    sync,
    downloadForOffline,
    resolveConflict,
    clearOfflineData,
    offlineStats,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = (): OfflineContextValue => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};
